import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Car, RefreshCw } from "lucide-react";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DOW = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmt = (s) => { if (!s) return "Tutte le date"; const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };

function monthCells(y, m) {
  const first = new Date(y, m, 1);
  const start = new Date(y, m, 1 - ((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

export function DayCalendar({ dateFrom, dateTo, today, count, onPick, onRefresh }) {
  const base = dateFrom || today;
  const [open, setOpen] = useState(false);
  const [ym, setYm] = useState([Number(base.slice(0, 4)), Number(base.slice(5, 7)) - 1]);
  const [busy, setBusy] = useState(new Set());
  const cells = monthCells(ym[0], ym[1]);

  useEffect(() => {
    if (open) { setYm([Number(base.slice(0, 4)), Number(base.slice(5, 7)) - 1]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) { return; }
    api.get("/bookings", { params: { date_from: iso(cells[0]), date_to: iso(cells[41]) } })
      .then((r) => setBusy(new Set((r.data || []).filter((b) => b.status !== "Cancellata").map((b) => b.date))))
      .catch(() => setBusy(new Set()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ym[0], ym[1]]);

  const shift = (n) => setYm(([y, m]) => { const d = new Date(y, m + n, 1); return [d.getFullYear(), d.getMonth()]; });
  const pick = (d) => { onPick(d); setOpen(false); };
  const tomorrow = (() => { const [y, m, d] = today.split("-").map(Number); return iso(new Date(y, m - 1, d + 1)); })();
  const single = dateFrom && dateFrom === dateTo;
  const label = single ? fmt(dateFrom) : `${fmt(dateFrom)}${dateTo && dateTo !== dateFrom ? ` → ${fmt(dateTo)}` : ""}`;

  return (
    <div className="pl-panel p-0 relative no-print" data-testid="day-calendar">
      <div className="flex items-center gap-3 px-4 h-14">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="day-calendar-toggle" className="flex items-center gap-2 text-[#F3EDE0] min-w-0">
          <CalendarDays size={20} className="text-[#D6A62A] shrink-0" />
          <span className="font-mono-num font-semibold text-base truncate" data-testid="day-calendar-label">{label}</span>
          <ChevronDown size={16} className={`text-[#D6A62A] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <span className="w-px h-6 bg-[#D6A62A]/30" />
        <div className="flex items-center gap-2 text-[#F3EDE0] text-sm" data-testid="day-calendar-count"><Car size={18} className="text-[#D6A62A]" /> Servizi: <b className="font-mono-num">{count}</b></div>
        <button type="button" onClick={onRefresh} title="Aggiorna" data-testid="day-calendar-refresh" className="ml-auto p-2 rounded-full text-[#D6A62A] hover:bg-[#D6A62A]/10 transition-colors"><RefreshCw size={18} /></button>
      </div>
      {open && (
        <div className="absolute left-0 top-full mt-2 z-30 w-[min(360px,calc(100vw-2rem))] pl-panel pl-pop p-4 luxe-fade-in" data-testid="day-calendar-popover">
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={() => shift(-1)} data-testid="day-calendar-prev" className="p-1.5 text-[#D6A62A] hover:bg-[#D6A62A]/10 rounded-full"><ChevronLeft size={18} /></button>
            <div className="font-display text-lg text-[#F3EDE0]" data-testid="day-calendar-month">{MONTHS[ym[1]]} {ym[0]}</div>
            <button type="button" onClick={() => shift(1)} data-testid="day-calendar-next" className="p-1.5 text-[#D6A62A] hover:bg-[#D6A62A]/10 rounded-full"><ChevronRight size={18} /></button>
          </div>
          <div className="grid grid-cols-7 text-center text-[11px] text-[#A8A39A] mb-1">{DOW.map((d) => <div key={d}>{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-y-1 text-center">
            {cells.map((d) => {
              const s = iso(d); const inMonth = d.getMonth() === ym[1]; const sel = single && s === dateFrom;
              return (
                <button key={s} type="button" onClick={() => pick(s)} data-testid={`day-calendar-day-${s}`}
                  className={`relative mx-auto w-10 h-10 rounded-full text-sm font-mono-num transition-colors ${sel ? "border border-[#F1C75B] bg-[#D6A62A]/20 text-[#FFE08A] font-bold" : inMonth ? "text-[#F3EDE0] hover:bg-[#D6A62A]/10" : "text-[#5f5a52] hover:bg-[#D6A62A]/5"} ${s === today && !sel ? "ring-1 ring-[#D6A62A]/40" : ""}`}>
                  {d.getDate()}
                  {busy.has(s) && <span className="absolute left-1/2 -translate-x-1/2 bottom-1 w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#D6A62A]/15">
            <button type="button" onClick={() => pick(today)} data-testid="day-calendar-today" className="btn-gold-primary px-4 h-9 rounded-lg text-sm font-bold">Oggi</button>
            <button type="button" onClick={() => pick(tomorrow)} data-testid="day-calendar-tomorrow" className="px-4 h-9 rounded-lg text-sm border border-[#D6A62A]/50 text-[#F3EDE0] hover:bg-[#D6A62A]/10">Domani</button>
            <span className="ml-auto flex items-center gap-1.5 text-xs text-[#A8A39A]"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Giorni con servizi</span>
          </div>
        </div>
      )}
    </div>
  );
}
